/**
 * Stream processors for handling SSE responses
 * Provides mode-specific processing for study/practice (simple) and teacher-tools (typed events)
 */

/**
 * Factory function to create the appropriate stream processor based on mode
 * @param {string} mode - The chat mode ('study', 'practice', or 'teacher-tools')
 * @returns {Object} Stream processor with processChunk and reset methods
 */
export function createStreamProcessor(mode) {
  if (mode === 'teacher-tools') {
    return createTypedEventProcessor();
  }
  return createSimpleProcessor();
}

/**
 * Creates a simple processor for study/practice modes
 * Handles plain text streaming without typed events
 * @returns {Object} Processor with processChunk and reset methods
 */
function createSimpleProcessor() {
  let fullResponse = '';
  let lineBuffer = '';
  let currentDataLines = [];
  let previousChunk = '';
  let currentEvent = null;

  function flushFrameResults(results) {
    // Join multiple data lines per SSE frame
    const payload = currentDataLines.join('\n');
    currentDataLines = [];
    const frameEvent = currentEvent;
    currentEvent = null;

    // Backend signals a fatal LLM/stream error with `event: error`.
    // The `data:` payload is technical (raw provider message) and must NOT be
    // rendered as assistant content. Surface it to onError so the UI can show
    // a generic localized message; log the raw payload for debugging.
    if (frameEvent === 'error') {
      if (payload) {
        console.error('[chatbot SSE] stream error event payload:', payload);
      }
      results.push({ type: 'error', rawData: payload });
      return;
    }

    if (payload === '') {
      // Treat empty data payload as an explicit newline
      fullResponse += '\n';
      results.push({ type: 'chunk', assistantText: fullResponse, delta: '\n' });
      previousChunk = '\n';
      return;
    }

    // Check for MESSAGE_COMPLETE marker with message ID
    if (payload.startsWith('[MESSAGE_COMPLETE]:')) {
      const jsonStr = payload.slice('[MESSAGE_COMPLETE]:'.length);
      try {
        const metadata = JSON.parse(jsonStr);
        results.push({ type: 'message_complete', messageId: metadata.messageId });
      } catch (e) {
        console.warn('Failed to parse MESSAGE_COMPLETE payload:', e);
      }
      return;
    }

    if (payload.includes('[DONE]')) {
      results.push({ type: 'complete', assistantText: fullResponse });
      return;
    }

    // Simply append the payload - it already contains the correct newlines from the source
    fullResponse += payload;
    results.push({ type: 'chunk', assistantText: fullResponse, delta: payload });
    previousChunk = payload;
  }

  return {
    /**
     * Process a chunk of streaming data
     * @param {string} chunk - Raw chunk from the stream
     * @returns {Object|Array<Object>} Result(s) with type, text, and optional delta
     */
    processChunk(chunk) {
      lineBuffer += chunk;
      const lines = lineBuffer.split(/\r?\n/);
      lineBuffer = lines.pop() || '';

      const results = [];
      let inDataContent = false;

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];

        // Empty line handling - context-aware
        if (line === '') {
          if (inDataContent && currentDataLines.length > 0) {
            // Check if this empty line is content or frame boundary
            const nextLine = lines[i + 1];

            if (nextLine === undefined || nextLine === '' || nextLine.startsWith('data:') || nextLine.startsWith('event:') || nextLine.startsWith(':')) {
              // This is the frame boundary
              flushFrameResults(results);
              inDataContent = false;
            } else {
              // This empty line represents a newline in the content
              currentDataLines.push(''); // Preserve the newline
            }
          } else if (currentDataLines.length > 0) {
            // Not in data content but have accumulated data - flush
            flushFrameResults(results);
          }
          continue;
        }

        // Skip SSE comments
        if (line.startsWith(':')) continue;

        // Parse data line: allow optional single leading space
        if (line.startsWith('data:')) {
          let data = line.slice(5);
          if (data.startsWith(' ')) data = data.slice(1);
          currentDataLines.push(data);
          inDataContent = true;
          continue;
        }

        // Event type lines reset data content flag and capture the frame's event name
        if (line.startsWith('event:')) {
          inDataContent = false;
          currentEvent = line.slice(6).trim();
          continue;
        }

        // Defensive: treat unknown lines as data
        currentDataLines.push(line);
        inDataContent = true;
      }

      // Return results accumulated for any complete frames in this chunk
      if (results.length === 0) return null;
      return results.length === 1 ? results[0] : results;
    },

    /**
     * Reset the processor state
     */
    reset() {
      fullResponse = '';
      lineBuffer = '';
      currentDataLines = [];
      previousChunk = '';
      currentEvent = null;
    },

    /**
     * Get current state (for debugging)
     */
    getState() {
      return { fullResponse, lineBuffer, previousChunk };
    },
  };
}

/**
 * Creates a typed event processor for teacher-tools mode
 * Handles SSE with named events (assistant_delta, canvas_delta, etc.)
 * Per SSE spec, multiple data: lines in a single event frame are joined with \n
 * @returns {Object} Processor with processChunk and reset methods
 */
function createTypedEventProcessor() {
  let assistantBuffer = '';
  let canvasBuffer = '';
  let lineBuffer = '';
  let currentEvent = null;
  let currentDataLines = []; // Accumulate data lines within a single event frame

  /**
   * Process event data based on event type
   * @param {string} eventType - The event type (or null for legacy)
   * @param {string} data - The data payload (already joined with \n for multi-line)
   * @returns {Object|null} Result object or null if no action needed
   */
  function processEventData(eventType, data) {
    switch (eventType) {
      case 'assistant_delta': {
        assistantBuffer += data;
        return {
          type: 'assistant',
          text: assistantBuffer,
          delta: data,
        };
      }

      case 'canvas_delta': {
        canvasBuffer += data;
        return {
          type: 'canvas',
          content: canvasBuffer,
          delta: data,
        };
      }

      case 'message_complete': {
        // Parse message ID from the data payload
        try {
          const metadata = JSON.parse(data);
          return {
            type: 'message_complete',
            messageId: metadata.messageId,
          };
        } catch (e) {
          console.warn('Failed to parse message_complete data:', e);
          return null;
        }
      }

      case 'done':
      case 'end':
        return {
          type: 'complete',
          assistantText: assistantBuffer,
          canvasContent: canvasBuffer,
        };

      case 'error': {
        // Backend signals a fatal LLM/stream error. The `data:` payload is
        // technical (raw provider message); surface it to onError so the UI
        // shows a generic localized message. Log the raw payload for debug.
        if (data) {
          console.error('[chatbot SSE] stream error event payload:', data);
        }
        return { type: 'error', rawData: data };
      }

      default:
        // Fallback for untyped data (backwards compatibility)
        if (!eventType && data) {
          assistantBuffer += data;
          return {
            type: 'assistant',
            text: assistantBuffer,
            delta: data,
          };
        }
        return null;
    }
  }

  /**
   * Flush accumulated data lines for the current event frame
   * Per SSE spec, multiple data: lines are joined with \n
   * @param {Array} results - Array to push results to
   */
  function flushEventFrame(results) {
    if (currentDataLines.length === 0) return;

    // Join all data lines with \n (SSE specification)
    // This ensures proper newlines are preserved between lines
    const payload = currentDataLines.join('\n');
    currentDataLines = [];

    const result = processEventData(currentEvent, payload);
    if (result) {
      results.push(result);
    }
  }

  return {
    /**
     * Process a chunk of streaming data with typed events
     * @param {string} chunk - Raw chunk from the stream
     * @returns {Array<Object>} Array of results (can have multiple events per chunk)
     */
    processChunk(chunk) {
      lineBuffer += chunk;
      const lines = lineBuffer.split(/\r?\n/);
      // Keep the last incomplete line in the buffer
      lineBuffer = lines.pop() || '';

      const results = [];

      for (const line of lines) {
        // Empty line signals end of SSE frame - flush accumulated data
        if (line === '') {
          flushEventFrame(results);
          currentEvent = null;
          continue;
        }

        // Skip SSE comments
        if (line.startsWith(':')) continue;

        // Parse event type - flush previous frame data first if switching events
        if (line.startsWith('event:')) {
          flushEventFrame(results);
          currentEvent = line.slice(6).trim();
        }
        // Parse data - accumulate for current frame
        else if (line.startsWith('data:')) {
          // Extract data and remove single leading space if present
          let data = line.slice(5);
          if (data.startsWith(' ')) {
            data = data.slice(1);
          }
          // Accumulate data lines (will be joined with \n on flush)
          currentDataLines.push(data);
        }
        // Handle malformed lines (shouldn't happen but defensive)
        else if (!currentEvent) {
          // Legacy format without event type - treat as assistant text
          currentDataLines.push(line);
        }
      }

      // Return array of results (could be empty, single, or multiple)
      return results.length === 1 ? results[0] : results;
    },

    /**
     * Reset the processor state
     */
    reset() {
      assistantBuffer = '';
      canvasBuffer = '';
      lineBuffer = '';
      currentEvent = null;
      currentDataLines = [];
    },

    /**
     * Get current state (for debugging)
     */
    getState() {
      return {
        assistantBuffer,
        canvasBuffer,
        lineBuffer,
        currentEvent,
        currentDataLines,
      };
    },
  };
}

/**
 * High-level function to process a stream using a processor
 * @param {ReadableStreamDefaultReader} reader - Stream reader
 * @param {Object} processor - Stream processor
 * @param {Object} callbacks - Callback functions (onChunk, onComplete, onError, onCanvasDelta, onMessageComplete)
 * @param {Object} options - Stream options (timeout)
 */
export async function processStream(reader, processor, callbacks, options = {}) {
  const decoder = new TextDecoder('utf-8');
  let isFirstMessage = true;

  // Setup stream timeout
  const timeoutMs = options.timeout || 30000;
  let timeoutId = setTimeout(() => {
    // Cancel the reader
    try {
      reader.cancel();
    } catch (e) {
      console.warn('Error cancelling reader on timeout:', e);
    }

    // Create timeout error
    const error = new Error('The request timed out. Please try again.');
    error.name = 'StreamTimeoutError';

    // Call error callback
    callbacks.onError(error);
  }, timeoutMs);

  try {
    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        // Stream ended without explicit completion signal
        // This shouldn't happen normally but handle gracefully
        callbacks.onComplete(processor.getState().fullResponse || processor.getState().assistantBuffer || '');
        break;
      }

      const chunk = decoder.decode(value, { stream: true });
      const result = processor.processChunk(chunk);

      // Handle single result or array of results
      const results = Array.isArray(result) ? result : [result];

      for (const res of results) {
        if (!res) continue;

        // Clear timeout on first message received (any type)
        if (isFirstMessage && timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
          isFirstMessage = false;
        }

        switch (res.type) {
          case 'chunk':
          case 'assistant':
            callbacks.onChunk(res.text || res.assistantText, res.delta);
            break;

          case 'canvas':
            if (callbacks.onCanvasDelta) {
              callbacks.onCanvasDelta(res.content);
            }
            break;

          case 'message_complete':
            if (callbacks.onMessageComplete) {
              callbacks.onMessageComplete(res.messageId);
            }
            break;

          case 'complete':
            // Send final canvas content FIRST (before completion cleanup)
            // This ensures canvas state is updated before clearStreamingCanvas() is called
            if (res.canvasContent && callbacks.onCanvasDelta) {
              callbacks.onCanvasDelta(res.canvasContent);
            }
            // Then signal completion - triggers cleanup including clearStreamingCanvas()
            callbacks.onComplete(res.assistantText || '');
            return; // Exit the loop

          case 'error': {
            // Stop the stream and notify the consumer with a tagged error so the
            // UI can show a generic localized message instead of the raw payload.
            const streamError = new Error('SSE stream error event');
            streamError.name = 'SSEStreamError';
            callbacks.onError(streamError);
            return; // Exit the loop
          }
        }
      }
    }
  } catch (error) {
    // Don't call onError for abort signals
    if (error.name !== 'AbortError') {
      callbacks.onError(error);
    }
  } finally {
    // Always cleanup timeout
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
}
